import React, { useEffect, useRef, useState, useMemo } from 'react';
import { AsyncTypeahead, ClearButton } from 'react-bootstrap-typeahead';
import { debounce } from 'lodash';
import { css } from 'emotion';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { useHistory } from 'react-router-dom';
import { LocationSearchModel } from '../../../../models/location-search.model';
import { OrgSearchModel } from '../../../../models/org-search.model';
import { UserModel } from '../../../../models/user.model';
import { setCurrentLocationId } from '../../../../redux/actions/location';
import { injectParams, ORG_DETAILS } from '../../../../constants/paths';
import { useDispatch } from 'react-redux';
import { weaveTheme } from '@weave/theme-original/dist/weaveTheme';
import { DropdownField, useFormField, SpinningLoader } from '@weave/design-system';
import {
  CombinedSearchResult,
  GroupedSearchMenu,
  SearchResultRow,
  SearchScopes,
} from './grouped-search-menu.component';

const typeaheadContainer = css`
  width: 100%;
  input.rbt-input-hint {
    display: none;
  }
  .rbt-aux {
    display: none !important;
  }
`;

const searchControls = css`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
`;

const searchTypeSelector = css`
  flex: 0 0 150px;
  position: relative;
`;

const searchInputContainer = css`
  position: relative;
  flex: 1;
`;

const searchContainer = css`
  width: 100%;
`;

const clearSearchButton = css`
  position: absolute;
  right: 12px;
  top: 8px;
`;

enum SearchTypes {
  Combined = 'combined',
  User = 'user',
  UserLocations = 'userLocations',
}

interface Props {
  loading: boolean;
  locations: LocationSearchModel[];
  userLocations: any;
  userSearchResults: UserModel[];
  orgs: OrgSearchModel[];
  className?: any;
  onSelect?: (location: LocationSearchModel) => void;
  locationSearch: (query: string) => void;
  userSearch: (props: { state: string }) => void;
  userLocationsSearch: (userId: string) => void;
  // orgSearch is backed by the IAM identity SearchOrgs gRPC endpoint (see
  // iam/internal/grpc/org.go), letting the lookup widget resolve an organization.
  orgSearch: (query: string) => void;
  // restrictToLocations disables org results, keeping this widget's behavior
  // location-only for embedded contexts (e.g. picking a parent location) where
  // selecting an organization would not make sense.
  restrictToLocations?: boolean;
}

export const LocationSearch = (props: Props) => {
  const typeahead = useRef<any>(null);
  const dispatch = useDispatch();
  const history = useHistory();
  const [searchType, setSearchType] = useState<SearchTypes>(SearchTypes.Combined);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDebouncing, setIsDebouncing] = useState(false);
  const searchScopeField = useFormField({
    type: 'dropdown',
    value: SearchScopes.All,
  });
  const searchScope = searchScopeField.value as SearchScopes;

  const debouncedSetSearchQuery = useMemo(
    () =>
      debounce((value: string) => {
        setSearchQuery(value);
        setIsDebouncing(false);
      }, 500),
    []
  );

  const handleSearchInputChange = (query: string) => {
    setIsDebouncing(true);
    debouncedSetSearchQuery(query);
  };

  useEffect(() => {
    return () => debouncedSetSearchQuery.cancel();
  }, [debouncedSetSearchQuery]);

  const setLocation = (locationId: string) => dispatch(setCurrentLocationId(locationId));

  useEffect(() => {
    searchQueryChanged();
  }, [searchQuery]);

  useEffect(() => {
    searchQueryChanged();
    typeahead.current?.getInstance().focus();
  }, [searchScope]);

  useEffect(() => {
    userSearchResultsChanged();
  }, [props.userSearchResults]);

  // The logic for selecting a user in the typeahead dropdown menu is different than
  // selecting a location. Since no location data is available yet, what will be done
  // instead is to set the full text of the selected username email as the input text
  // in the typeahead input component and trigger the appropriate events to simulate a
  // search using the full email.
  const handleUserSelected = (selectedUser: any[]) => {
    const username = selectedUser[0].Username;

    debouncedSetSearchQuery.cancel();
    setIsDebouncing(false);
    typeahead.current.getInstance().clear();
    typeahead.current.getInstance().setState({ text: username });
    setSearchQuery(username);
    typeahead.current.getInstance().blur();
    typeahead.current.getInstance().focus();
  };

  // Selecting an org navigates to the dedicated org details landing page, which
  // fetches and displays the full org hierarchy (via GetOrgHierarchy) and lets the
  // user click through to any of its locations. Selecting a location behaves as
  // before, calling onSelect or setting the current location directly.
  const handleCombinedSelection = (selections: CombinedSearchResult[]) => {
    debouncedSetSearchQuery.cancel();
    setIsDebouncing(false);
    const selection = selections[0];
    if (!selection) {
      return;
    }

    typeahead.current.getInstance().clear();

    if (selection.kind === 'org') {
      history.push(injectParams(ORG_DETAILS, { orgId: selection.OrgId }));
      return;
    }

    if (props.onSelect) {
      props.onSelect(selection);
    } else {
      setLocation(selection.LocationID);
    }
  };

  const handleSelection = (selections: any[]) => {
    debouncedSetSearchQuery.cancel();
    setIsDebouncing(false);
    const selection = selections[0];
    if (!selection) {
      return;
    }

    if (props.onSelect) {
      props.onSelect(selection);
    } else {
      setLocation(selection.LocationID);
    }

    typeahead.current.getInstance().clear();
  };

  const multiLocationSearch = () => {
    const userId = props.userSearchResults[0].UserID;
    props.userLocationsSearch(userId);
  };

  const handleSearch = (
    currentSearchType: SearchTypes,
    currentSearchScope = searchScope
  ) => {
    if (!searchQuery) {
      return;
    }

    if (currentSearchType === SearchTypes.Combined) {
      if (
        props.restrictToLocations ||
        currentSearchScope === SearchScopes.All ||
        currentSearchScope === SearchScopes.Location ||
        currentSearchScope === SearchScopes.IdSlug
      ) {
        props.locationSearch(searchQuery);
      }
      if (!props.restrictToLocations && currentSearchScope !== SearchScopes.Location) {
        props.orgSearch(searchQuery);
      }
    } else if (currentSearchType === SearchTypes.User) {
      props.userSearch({ state: searchQuery });
    } else if (currentSearchType === SearchTypes.UserLocations) {
      multiLocationSearch();
    }
  };

  const searchQueryChanged = () => {
    let type = searchType;

    if (searchQuery.includes('@') && searchScope === SearchScopes.All) {
      type = SearchTypes.User;
    } else {
      type = SearchTypes.Combined;
    }

    if (type !== searchType) {
      setSearchType(type);
    }

    handleSearch(type, searchScope);
  };

  const userSearchResultsChanged = () => {
    // When searching by user, if only one option is left, then set the searchType
    // as "userLocations" to search all locations associated with the user.
    if (props.userSearchResults.length === 1) {
      setSearchType(SearchTypes.UserLocations);
      handleSearch(SearchTypes.UserLocations);
    }
  };

  const allScopedResults: CombinedSearchResult[] = [
    ...(props.restrictToLocations || searchScope === SearchScopes.Location
      ? []
      : props.orgs.map((org) => ({ kind: 'org' as const, ...org }))),
    ...(searchScope === SearchScopes.Organization
      ? []
      : props.locations.map((location) => ({
          kind: 'location' as const,
          ...location,
        }))),
  ];
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const combinedResults =
    searchScope === SearchScopes.IdSlug
      ? allScopedResults.filter((result) =>
          result.kind === 'org'
            ? result.OrgId.toLowerCase() === normalizedQuery
            : result.LocationID.toLowerCase() === normalizedQuery ||
              result.Slug.toLowerCase() === normalizedQuery
        )
      : allScopedResults;

  const renderGroupedMenu = (results: CombinedSearchResult[], menuProps: any) => {
    return (
      <GroupedSearchMenu
        menuProps={menuProps}
        results={results}
        searchQuery={searchQuery}
        searchScope={searchScope}
        isLoading={!!props.loading || isDebouncing}
      />
    );
  };

  const typeaheadConfigurations = {
    combinedSearch: {
      useCache: false,
      filterBy: () => true,
      labelKey: (option: CombinedSearchResult) => {
        return option.kind === 'org'
          ? `${option.Name} (${option.OrgId})`
          : `${option.Name} (${option.Slug}) - (${option.LocationID})`;
      },
      onChange: handleCombinedSelection,
      options: props.loading || isDebouncing ? [] : combinedResults,
      renderMenu: props.restrictToLocations ? undefined : renderGroupedMenu,
      renderMenuItemChildren: (option: CombinedSearchResult) => (
        <SearchResultRow result={option} />
      ),
    },
    userSearch: {
      useCache: false,
      // Need to do exact match filtering when matching data from user search results.
      // The goal is to narrow down to only one user suggestion but without custom filtering
      // this might never happen since searching for "query@email.com" could match
      // "query@email.com" but also "name.query@email.com".
      filterBy: (option: UserModel, typeaheadProps: any) =>
        option.Username.startsWith(typeaheadProps.text),
      labelKey: (option: UserModel) =>
        `${option.FirstName} ${option.LastName} - ${option.Username}`,
      onChange: handleUserSelected,
      options: props.loading || isDebouncing ? [] : props.userSearchResults,
    },
    userLocationsSearch: {
      useCache: false,
      labelKey: (option: any) => `${option.Username} - ${option.Name}`,
      onChange: handleSelection,
      options: props.loading || isDebouncing ? [] : props.userLocations,
      renderMenuItemChildren: (option: any, typeaheadProps: any, index: number) => {
        return (
          <div>
            <div>
              {option.Name} - ({option.Slug})
            </div>
            <div style={{ fontSize: '10px' }}>{option.Username}</div>
          </div>
        );
      },
    },
  };

  const placeholders = {
    [SearchScopes.All]: 'Location or organization name, ID, slug or phone',
    [SearchScopes.Organization]: 'Organization name or ID',
    [SearchScopes.Location]: 'Location name, ID, slug or phone',
    [SearchScopes.IdSlug]: 'Location or organization ID / slug',
  };

  const getEmptyLabel = () => {
    if (props.restrictToLocations) return `No locations match '${searchQuery}'`;

    switch (searchScope) {
      case SearchScopes.Organization:
        return `No organizations match '${searchQuery}'`;
      case SearchScopes.Location:
        return `No locations match '${searchQuery}'`;
      default:
        return `No results match '${searchQuery}'`;
    }
  };

  const clearSearch = () => {
    debouncedSetSearchQuery.cancel();
    setIsDebouncing(false);
    typeahead.current?.getInstance().clear();
    setSearchQuery('');
  };

  return (
    <div className={`${searchContainer} ${props.className ?? ''}`}>
      <div className={searchControls}>
        {!props.restrictToLocations && (
          <DropdownField
            {...searchScopeField}
            className={searchTypeSelector}
            label=""
            name="search-type"
          >
            <DropdownField.Option value={SearchScopes.All}>
              Search: All
            </DropdownField.Option>
            <DropdownField.Option value={SearchScopes.Organization}>
              Search: Organization
            </DropdownField.Option>
            <DropdownField.Option value={SearchScopes.Location}>
              Search: Location
            </DropdownField.Option>
            <DropdownField.Option value={SearchScopes.IdSlug}>
              Search: ID / slug
            </DropdownField.Option>
          </DropdownField>
        )}
        <div className={searchInputContainer}>
          <AsyncTypeahead
            id="locationSearchBox"
            delay={300}
            placeholder={
              props.restrictToLocations
                ? 'Location name, ID, slug or phone'
                : placeholders[searchScope]
            }
            className={typeaheadContainer}
            ref={typeahead}
            highlightOnlyResult={true}
            isLoading={!!props.loading || isDebouncing}
            onSearch={handleSearchInputChange}
            searchText={
              <div style={{ display: 'flex', alignItems: 'center', padding: '8px 12px' }}>
                <SpinningLoader size="small" />
                <span
                  style={{
                    marginLeft: '8px',
                    fontSize: '13px',
                    color: weaveTheme.colors.gray600,
                  }}
                >
                  Searching...
                </span>
              </div>
            }
            {...typeaheadConfigurations[`${searchType}Search`]}
            autoFocus={true}
            emptyLabel={getEmptyLabel()}
          />
          {!!searchQuery && (
            <ClearButton
              className={clearSearchButton}
              label="Clear search"
              onClick={clearSearch}
            />
          )}
        </div>
      </div>
    </div>
  );
};
